/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect, FunctionComponent, useCallback } from 'react';
import Select from 'react-select';

import createAssets from '@api/v4/assets';
import { CanvaButtonApi, templateTypeDetails } from './canvaTypes';

import './styles/canva.scss';

let canvaAPI: CanvaButtonApi;

// Load the Canva API - Ensure we only do this once
const initCanva = (document, url): void => {
  const script = document.createElement('script');
  script.src = url;
  script.onload = (): void => {
    (async (): Promise<void> => {
      if (window.CanvaButton) {
        const api = await window.CanvaButton.initialize({
          apiKey: "rL0oMltMP2ORjYdrCQ-jwz3A" // TODO - Update this to use the ENV Canva API Key
        });
        canvaAPI = api;
      }
    })();
  };
  document.body.appendChild(script);
};
initCanva(document, 'https://sdk.canva.com/v2/beta/api.js');


interface CanvaButtonProps {
  onNewAssets: (response: any) => void;
  onAssetProcessing: (numAssets: number) => void;
  sectionKey: string;
  className?: string;
}

/** export for testing purposes */
export const getNameFromFilename = (filename: string): string => {
  const extStart = filename.lastIndexOf(".");
  return filename.substring(0, extStart);
};

/** export for testing purposes */
export const getFilenameFromUrl = (exportUrl: string): string => {
  const filenameIndex = exportUrl.indexOf('filename');
  const responseExpiresIndex = exportUrl.indexOf('&response-expires');
  const filenameComponent = decodeURIComponent(exportUrl.substring(filenameIndex, responseExpiresIndex));
  return decodeURIComponent(filenameComponent.replace("filename*=UTF-8''", ""));
};

const CanvaButton: FunctionComponent<CanvaButtonProps> = ({ className, sectionKey, onNewAssets, onAssetProcessing }: CanvaButtonProps) => {
  const [selectedOption, setSelectedOption] = useState(null);

  const onDesignOpen = ({ designId }): void => {
    // Triggered when editor finishes loading and opens a new design.
    // You can save designId for future use.
  };

  const onDesignPublish = useCallback(async ({ exportUrl, designId }): Promise<void> => {
    // Triggered when design is published to an image
    // Save the image to your server as the exportUrl will expire shortly.

    const source = `canva::${designId}`;
    const filename = getFilenameFromUrl(exportUrl);
    const name = getNameFromFilename(filename);
    const data = {
      attributes: [{
        name,
        attachments: [
          {
            url: exportUrl,
            filename,
            source
          }
        ],
      }]
    };
    try {
      onAssetProcessing(1);
      const isCollection = BFG.resource.type === 'collection';
      const url = isCollection
        ? `/api/v4/collections/${BFG.resource.key}/assets`
        : `/api/v4/brandfolders/${BFG.resource.key}/assets`;
      const response = await createAssets({ url, section_key: sectionKey, data });
      if (response) {
        setSelectedOption(null);
        onAssetProcessing(0);
        Notify.create({
          type: 'success',
          title: t("Asset Created"),
          body: "The template you saved from Canva has been added."
        });
        onNewAssets(response);
      }
    } catch (e) {
      setSelectedOption(null);
      onAssetProcessing(0);
      Notify.create({
        type: 'error',
        title: t("An error occurred while saving from Canva. Please try again."),
      });
    }
  }, []);

  useEffect(() => {
    if (selectedOption) {
      const designType = selectedOption.value;

      canvaAPI.createDesign({
        type: designType,
        fileType: selectedOption.fileType,
        publishLabel: t("Save to Brandfolder"),
        onDesignOpen,
        onDesignPublish
      });
    }
  }, [selectedOption, onDesignPublish]);

  return (
    <div className="canva-button-component">
      <Select
        value={selectedOption}
        onChange={(newSelectedOption): void => setSelectedOption(newSelectedOption)}
        options={templateTypeDetails}
        placeholder={t('Create from Canva')}
      />
    </div>
  );
};

export default CanvaButton;
